// src/sim/physics.ts -- pure
import type { Terrain } from './terrain';

export interface Projectile {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  weaponId: string;
  owner: number;
  isSub: boolean;
  mode: 'flying' | 'rolling' | 'tunneling';
  prevVy: number;
  bouncesLeft: number;
  tunnelTraveled: number;
}

export type EdgeMode = 'none' | 'wrap' | 'wall';

export interface World {
  gravity: number; // px/s^2 (e.g., 200)
  width: number;
  height: number;
  wind: number; // px/s^2 horizontal acceleration (-80..+80)
  edgeMode: EdgeMode;
}

export interface StepResult {
  type: 'flying' | 'hit' | 'hit-tank' | 'offscreen';
  x: number;
  y: number;
  tankIdx: number;
}

// Pre-allocated singleton to avoid per-call allocation (PRF-02)
const _result: StepResult = { type: 'flying', x: 0, y: 0, tankIdx: -1 };

/** Single-step Euler integration for one projectile. Mutates in place. */
export function stepProjectile(
  p: Projectile,
  terrain: Terrain,
  world: World,
  tanks: ReadonlyArray<{ x: number; y: number; alive: boolean }>,
  dt: number,
): StepResult {
  _result.type = 'flying';
  _result.x = p.x;
  _result.y = p.y;
  _result.tankIdx = -1;

  if (!p.active) return _result;

  // Track previous vy for MIRV apex detection
  p.prevVy = p.vy;

  // Apply wind ONCE per full step (outside substep loop) to avoid 4x amplification
  p.vx += world.wind * dt;

  // Substep collision: divide dt into N micro-steps so a fast projectile
  // doesn't tunnel through a 1-px ridge. 4 substeps is plenty at 60 Hz for
  // velocities up to ~960 px/s.
  const SUBSTEPS = 4;
  const sdt = dt / SUBSTEPS;

  for (let i = 0; i < SUBSTEPS; i++) {
    p.vy += world.gravity * sdt;
    p.x += p.vx * sdt;
    p.y += p.vy * sdt;

    // Bottom edge always kills regardless of edge mode
    if (p.y >= world.height) {
      p.active = false;
      _result.type = 'offscreen';
      _result.x = p.x;
      _result.y = p.y;
      return _result;
    }

    // Side-edge handling based on edge mode
    if (p.x < 0 || p.x >= world.width) {
      switch (world.edgeMode) {
        case 'none':
          p.active = false;
          _result.type = 'offscreen';
          _result.x = p.x;
          _result.y = p.y;
          return _result;
        case 'wrap':
          p.x = ((p.x % world.width) + world.width) % world.width;
          break;
        case 'wall':
          p.x = p.x < 0 ? 0 : world.width - 1;
          p.vx = -p.vx;
          break;
      }
    }

    // Top: allow above-screen flight
    if (p.y < 0) continue;

    // Check tank hitboxes before terrain (so a direct hit on tank registers)
    for (let t = 0; t < tanks.length; t++) {
      const tank = tanks[t];
      if (!tank.alive) continue;
      // Tank hitbox: 12x6 rectangle centered at (tank.x, tank.y - 1)
      if (
        p.x >= tank.x - 6 &&
        p.x <= tank.x + 6 &&
        p.y >= tank.y - 4 &&
        p.y <= tank.y + 2
      ) {
        p.active = false;
        _result.type = 'hit-tank';
        _result.x = p.x;
        _result.y = p.y;
        _result.tankIdx = t;
        return _result;
      }
    }

    if (terrain.isSolid(p.x, p.y)) {
      p.active = false;
      _result.type = 'hit';
      _result.x = p.x;
      _result.y = p.y;
      return _result;
    }
  }

  _result.x = p.x;
  _result.y = p.y;
  return _result;
}
